import { useState } from 'react';
import { Button, InputNumber, message } from 'antd';
import { Banknote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { confirmOrderAmount } from '@/api/orderService';
import { AmountRequest } from '@/types/order';

interface AmountRequestCardProps {
  orderUid: string;
  request: AmountRequest;
}

/**
 * BOXY-888 — the DSP driver asked to change the amount (price change or part delivery). The merchant
 * confirms the final amount; the order closes at it.
 */
export function AmountRequestCard({ orderUid, request }: AmountRequestCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<number | null>(request.amount_iqd);
  const [error, setError] = useState<string | null>(null);

  const confirm = useMutation(() => confirmOrderAmount(orderUid, Number(amount)), {
    onSuccess: () => {
      setError(null);
      message.success(String(t('amount_request_confirmed')));
      queryClient.invalidateQueries(['order', orderUid]);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) =>
      setError(err?.response?.data?.message || err?.message || String(t('operation_failed'))),
  });

  return (
    <section
      aria-label={String(t('amount_request_title'))}
      className='mb-4 flex items-start gap-3 rounded-2xl border border-solid border-amber-300 bg-amber-50 px-4 py-3.5'
    >
      <Banknote className='mt-0.5 h-4 w-4 shrink-0 text-amber-700' />
      <div className='min-w-0 flex-1'>
        <p className='m-0 text-[13px] font-bold text-sd-foreground'>
          {String(t('amount_request_title'))} · {String(t(`amount_request_${request.request}`))}
        </p>
        {request.customer_said ? (
          <p className='m-0 mt-1 break-words text-[13px] text-sd-foreground'>
            {String(t('amount_request_customer_said'))}: “{request.customer_said}”
          </p>
        ) : null}
        <label
          className='mt-3 block text-[12px] text-sd-muted-foreground'
          htmlFor='amount-request-input'
        >
          {String(t('amount_request_amount_label'))}
        </label>
        <div className='mt-1 flex flex-wrap items-center gap-2'>
          <InputNumber
            id='amount-request-input'
            min={0}
            precision={0}
            value={amount}
            onChange={(v) => setAmount(v)}
            className='w-48'
          />
          <Button
            type='primary'
            loading={confirm.isLoading}
            disabled={amount === null}
            onClick={() => confirm.mutate()}
          >
            {String(t('amount_request_confirm'))}
          </Button>
        </div>
        {error ? (
          <p role='alert' className='m-0 mt-2 text-[12px] text-sd-destructive'>
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default AmountRequestCard;
